/**
 * Modal di modifica dei tag di un titolo (F-079).
 *
 * **Richiamabile da qualunque sezione**, come chiede il TODO: riceve solo
 * l'id e il nome del titolo più i tag correnti, e non sa da dove è stato
 * aperto. Il salvataggio invalida per conto proprio tutto ciò che mostra tag
 * (vedi `useSaveTags`), quindi il chiamante non deve ricordarsi di aggiornare
 * nulla — che è il modo in cui una schermata resta indietro.
 *
 * ## Tre scelte che rendono i tag utilizzabili invece che solo possibili
 *
 * 1. **Si lavora su una bozza locale**, non sul server a ogni click: togliere
 *    tre tag e ripensarci non deve costare tre chiamate né lasciare uno stato
 *    intermedio salvato. Si salva una volta, con l'insieme finale.
 * 2. **I tag già usati sono proposti**, dal più frequente. Senza, la stessa
 *    idea nasce come «energia», «energetico» ed «energy» — tre tag che non si
 *    incontrano mai, e la vista per tag diventa inutile.
 * 3. **La normalizzazione è mostrata mentre si scrive**: il chip appare
 *    minuscolo appena aggiunto, così l'utente vede subito che «USA» diventa
 *    «usa» invece di scoprirlo dopo il salvataggio.
 */

import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError } from '@/lib/api-client';
import { Alert, Button, Field, Modal } from '@/ui';
import { useAllTags, useSaveTags } from './api';
import { MAX_TAGS_PER_INSTRUMENT, TAG_MAX_LENGTH, normalizeTag } from './normalize';
import { TagList } from './TagList';
import './tags.css';

type TagsModalProps = {
  instrumentId: string;
  instrumentName: string;
  currentTags: string[];
  onClose: () => void;
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

export function TagsModal({ instrumentId, instrumentName, currentTags, onClose }: TagsModalProps) {
  const [draft, setDraft] = useState<string[]>(() => [...currentTags]);
  const [input, setInput] = useState('');
  const save = useSaveTags(instrumentId);
  const known = useAllTags();

  const suggestions = useMemo(() => {
    const already = new Set(draft);
    return (known.data?.items ?? []).map((item) => item.tag).filter((tag) => !already.has(tag)).slice(0, 12);
  }, [known.data, draft]);

  const add = (raw: string) => {
    const tag = normalizeTag(raw);
    // Un tag già presente non è un errore da segnalare: è già lì, e basta.
    if (!tag || draft.includes(tag) || draft.length >= MAX_TAGS_PER_INSTRUMENT) return;
    setDraft([...draft, tag].sort((left, right) => left.localeCompare(right, 'it')));
    setInput('');
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(draft, { onSuccess: onClose });
  };

  return (
    <Modal title={`Tag — ${instrumentName}`} onClose={onClose}>
      <form onSubmit={submit} className="tags-modal">
        {save.isError && (
          <Alert variant="error" title="Tag non salvati">
            {errorMessage(save.error)}
          </Alert>
        )}

        <div className="tags-modal__current">
          {draft.length === 0 ? (
            <p className="tags-modal__hint">Nessun tag: aggiungine uno qui sotto.</p>
          ) : (
            <TagList tags={draft} onRemove={(tag) => setDraft(draft.filter((item) => item !== tag))} />
          )}
        </div>

        <Field
          label="Aggiungi un tag"
          value={input}
          maxLength={TAG_MAX_LENGTH}
          placeholder="es. ai, usa, energia"
          hint={
            draft.length >= MAX_TAGS_PER_INSTRUMENT
              ? `Massimo ${MAX_TAGS_PER_INSTRUMENT} tag per titolo.`
              : 'Invio per aggiungere. Diventa minuscolo: «USA» e «usa» sono lo stesso tag.'
          }
          disabled={draft.length >= MAX_TAGS_PER_INSTRUMENT}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            // Invio aggiunge il tag e NON invia il form: altrimenti il primo
            // tag scritto chiuderebbe il modal salvando senza gli altri.
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              add(input);
            }
          }}
        />

        {suggestions.length > 0 && (
          <div className="tags-modal__suggestions">
            <p className="tags-modal__hint">Già usati:</p>
            <ul className="tag-list">
              {suggestions.map((tag) => (
                <li key={tag}>
                  <button type="button" className="tag tag--suggestion" onClick={() => add(tag)}>
                    + {tag}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <footer className="tags-modal__actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" loading={save.isPending}>
            Salva
          </Button>
        </footer>
      </form>
    </Modal>
  );
}
